<template>
	<div class="external-api-key-permissions-editor">
		<div class="permissions-section">
			<TypographyText strong>{{ $t('account.externalApiKeys.permissionGroups.operations') }}</TypographyText>
			<CheckboxGroup :value="operationsValue" :options="operationOptions" @change="onChangeOperations" />
		</div>
		<div class="permissions-section">
			<TypographyText strong>{{ $t('account.externalApiKeys.permissionGroups.domains') }}</TypographyText>
			<CheckboxGroup :value="domainsValue" :options="domainOptions" @change="onChangeDomains" />
			<div v-if="domainErrorVisible" class="domain-error">
				{{ $t('account.externalApiKeys.domainPermissionRequired') }}
			</div>
		</div>
		<div class="permissions-section">
			<TypographyText strong>{{ $t('account.externalApiKeys.permissionsPreviewLabel') }}</TypographyText>
			<div class="permissions-preview">
				<div v-for="previewKey in previewKeys" :key="previewKey">
					{{ $t('account.externalApiKeys.permissionsPreview.' + previewKey) }}
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { computed } from 'vue';
import { CheckboxGroup, TypographyText } from 'ant-design-vue';

import { useI18n } from 'vue-i18n';

import {
	API_KEY_PERMISSION_PROFILES,
	API_KEY_PERMISSION_WRITE,
	buildPermissionsPreviewKeys,
} from '@/components/account/externalApiKeyPermissions';


const OPERATION_PERMISSIONS = [API_KEY_PERMISSION_WRITE];
const DOMAIN_PERMISSIONS_IN_UI = [API_KEY_PERMISSION_PROFILES];


export default {
	components: {
		CheckboxGroup,
		TypographyText,
	},
	props: {
		permissions: {
			type: Array,
			required: true,
		},
		domainErrorVisible: {
			type: Boolean,
			default: false,
		},
	},
	emits: ['update:permissions'],
	setup(props, { emit }) {
		const { t } = useI18n();

		const operationOptions = computed(() => ([
			{ label: t('account.externalApiKeys.permissionLabels.write'), value: API_KEY_PERMISSION_WRITE },
		]));

		const domainOptions = computed(() => ([
			{ label: t('account.externalApiKeys.permissionLabels.profiles'), value: API_KEY_PERMISSION_PROFILES },
		]));

		const operationsValue = computed(() => props.permissions.filter(permission => OPERATION_PERMISSIONS.includes(permission)));
		const domainsValue = computed(() => props.permissions.filter(permission => DOMAIN_PERMISSIONS_IN_UI.includes(permission)));
		const previewKeys = computed(() => buildPermissionsPreviewKeys(props.permissions));

		const emitMerged = (operations, domains) => {
			const otherPermissions = props.permissions.filter(permission =>
				!OPERATION_PERMISSIONS.includes(permission) && !DOMAIN_PERMISSIONS_IN_UI.includes(permission),
			);
			emit('update:permissions', [...operations, ...domains, ...otherPermissions]);
		};

		const onChangeOperations = nextOperations => {
			emitMerged(nextOperations, domainsValue.value);
		};

		const onChangeDomains = nextDomains => {
			emitMerged(operationsValue.value, nextDomains);
		};

		return {
			operationOptions,
			domainOptions,
			operationsValue,
			domainsValue,
			previewKeys,
			onChangeOperations,
			onChangeDomains,
		};
	},
};
</script>

<style scoped>
.external-api-key-permissions-editor {
	display: flex;
	flex-direction: column;
	gap: 12px;
}

.permissions-section {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.permissions-preview {
	display: flex;
	flex-direction: column;
	gap: 2px;
}

.domain-error {
	color: #ff4d4f;
	font-size: 12px;
}
</style>
